/// <amd-module name="Wit/QueriesHub/Editor/QueryEditor" />

import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableValue, ObservableArray, ObservableValue, ReadyableObservableArray } from "VSS/Core/Observable";
import { IPerformanceScenarioPendingWork, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { IListSelection, ListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { Splitter, SplitterDirection } from "VSSUI/Splitter";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITreeRow, Tree } from "VSSUI/TreeEx";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IQueryService } from "Wit/Common/QueryService/IQueryService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import { LinkQueryMode } from "Wit/Common/QueryUtils/QueryTypeConstants";
import { WiqlOperators } from "Wit/Common/WiqlOperators";
import { BoardsMessages } from "Wit/Messages/Components/BoardsMessages";
import { QueryHeader } from "Wit/QueriesHub/Common/Components/QueryHeader";
import { ClauseType, IClause, IEditInfo, IFilter, IQueryResultItem, IQueryTypeItem, IWorkItemLinkType } from "Wit/QueriesHub/Common/Interfaces";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";
import { ResultsTree } from "Wit/QueriesHub/ResultsView/ResultsTree";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { IFieldProjectData, INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { EditorTable } from "./EditorTable";
import { LinkedQueryEditor } from "./LinkedQueryEditor";
import { TreeQueryEditor } from "./TreeQueryEditor";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ScreenSize } from "VSS/Core/Util/Screen";


interface IQueryEditorProps {
    isShowMoreItemsSet?: ObservableValue<boolean>;
}

export class QueryEditor extends VssComponent<IQueryEditorProps> {
    private editInfo: IObservableValue<IEditInfo>;
    private editorKey = new ObservableValue<number>(0);
    private fieldProjectData: IObservableValue<IFieldProjectData>;
    private fields: ReadyableObservableArray<IFieldEntry>;
    private isDirty: IObservableValue<boolean>;
    private isQueryLoading = new ObservableValue(true);
    private lastFocusedRow = new ObservableValue<number>(-1);
    private projectId: string;
    private projectName: string;
    private queryAcrossProjectsChecked: IObservableValue<boolean>;
    private queryService: IQueryService;
    private shortcutService: IVssKeyboardShortcutService;
    private globalMessageService: IGlobalMessagesService;
    private queryType = new ObservableValue<QueryType>(QueryType.Flat);
    private queryTypeItems: IListBoxItem<QueryType>[];
    private queryTypeSelection: DropdownSelection;
    private selection = new ListSelection(true);
    private splitterSize: number | undefined;
    private sourceIds = new ObservableArray<number>([]);
    private targetIds = new ObservableArray<number>([]);
    private treeRef = React.createRef<Tree<IQueryResultItem>>();
    private workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    private workItemLinkTypes: ObservableArray<IWorkItemLinkType>;
    private pendingWork: IPerformanceScenarioPendingWork | undefined;
    private resultsDivRef = React.createRef<HTMLDivElement>();
    private queryResultsType = new ObservableValue<QueryType>(QueryType.Flat);
    private enableNewBoardsQueriesNewDesignFF: boolean;

    constructor(props: IQueryEditorProps, context: IVssComponentContext) {
        super(props, context);
        this.queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        this.globalMessageService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        this.workItemTrackingDataProvider = new WorkItemTrackingDataProvider(this.context.pageContext);
        this.shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        this.createQueryTypeItems();
        this.queryAcrossProjectsChecked = new ObservableValue<boolean>(false);
        this.projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        this.projectName = getTFSPageData(this.context.pageContext)?.project?.name ?? "";
        this.editInfo = this.queryService.editInfo;
        this.isDirty = this.queryService.isEditorDirty;
        this.fieldProjectData = this.queryService.fieldProjectData;
        this.fields = this.queryService.editorFields;
        this.workItemLinkTypes = this.queryService.editorWorkItemLinkTypes;
        if (this.fields.value.length === 0) {
            this.queryService.initializeFields(this.projectId);
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const splitterSizeSettings: { [key: string]: string } | undefined = settingsService.getEntry(
            QueriesHubConstants.EditorTableSplitterSize,
            SettingsUserScope.Me
        );

        if (splitterSizeSettings && splitterSizeSettings[QueriesHubConstants.SettingsKey]) {
            this.splitterSize = Number(splitterSizeSettings[QueriesHubConstants.SettingsKey]) ?? undefined;
        }

        this.enableNewBoardsQueriesNewDesignFF = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign);
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.loadData();
        this.onQueryResultUpdated();
        this.queryService.addResultListeners();
        this.registerShortcuts();
        this.editInfo.subscribe(this.updateCrossProjectCheckbox);
        this.pendingWork?.complete();
    }

    public componentWillUnmount(): void {
        this.unregisterShortcuts();
        this.queryService.removeResultListeners();
        this.editInfo.unsubscribe(this.updateCrossProjectCheckbox);
    }

    public render(): JSX.Element {
        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    if (!this.pendingWork) {
                        this.pendingWork = scenarioProps.registerPendingWork(QueriesHubConstants.EditorTabViewed);
                    }

                    return (
                        <Surface background={SurfaceBackground.neutral}>
                            <Page className={this.enableNewBoardsQueriesNewDesignFF ? "flex-grow flex-column" : "query-editor-page flex-grow flex-column"}>
                                <BoardsMessages className="boards-message-bar flex-noshrink" />
                                <QueryHeader
                                    hideFocusArrows={true}
                                    selectedTabId="editor"
                                    selection={this.selection}
                                    lastFocusedRow={this.lastFocusedRow}
                                    onRunQuery={this.onRunQuery}
                                    onFocusNext={this.onFocusNext}
                                    onFocusPrevious={this.onFocusPrevious}
                                    isDirty={this.isDirty}
                                    onRevert={this.onRevert}
                                    queryResultItems={this.queryService.queryResultItems}
                                    workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                />

                                {this.enableNewBoardsQueriesNewDesignFF ?
                                    <Observer
                                        crossProject={this.queryAcrossProjectsChecked}
                                        isLoaded={this.fields.ready}
                                        isQueryLoading={this.isQueryLoading}
                                    >
                                        {(observableProps: { crossProject: boolean; isLoaded: boolean; isQueryLoading: boolean }) => {

                                            const pageContent = (
                                                <Observer
                                                    fieldProjectData={this.fieldProjectData}
                                                    fields={this.fields}
                                                    editInfo={this.editInfo}
                                                    editorKey={this.editorKey}
                                                >
                                                    {(observerProps: {
                                                        fieldProjectData: IFieldProjectData;
                                                        fields: IFieldEntry[];
                                                        editInfo: IEditInfo;
                                                        editorKey: number;
                                                    }) => {
                                                        return (
                                                            <div className="flex-column page-content page-content-top full-height separator-line-top">
                                                                <div className="rhythm-vertical-16">
                                                                    <span className="editor-content-header">{Resources.QueriesEditorTopLevelText}</span>
                                                                    <EditorTable
                                                                        key={"editor-table-" + observerProps.editorKey}
                                                                        clauseType={ClauseType.Source}
                                                                        crossProject={
                                                                            observerProps.editInfo.teamProject === undefined ||
                                                                            observerProps.editInfo.teamProject.length === 0
                                                                        }
                                                                        editInfo={observerProps.editInfo}
                                                                        fieldProjectData={observerProps.fieldProjectData}
                                                                        fields={observerProps.fields}
                                                                        projectId={this.projectId}
                                                                        setIsDirty={this.setIsDirty}
                                                                        setFilterState={this.setFilterState}
                                                                        setStateAddRow={this.setStateAddRow}
                                                                        setStateRemoveRow={this.setStateRemoveRow}
                                                                        setClauseFieldState={this.setClauseFieldState}
                                                                        setClauseOperatorState={this.setClauseOperatorState}
                                                                        setClauseValueState={this.setClauseValueState}
                                                                        setClauseLogicalOperatorState={this.setClauseLogicalOperatorState}
                                                                    />
                                                                </div>
                                                                <Observer
                                                                    queryType={this.queryType}
                                                                    workItemLinkTypes={this.workItemLinkTypes}
                                                                >
                                                                    {(props: {
                                                                        queryType: QueryType;
                                                                        workItemLinkTypes: IWorkItemLinkType[];
                                                                    }) => (
                                                                        <>
                                                                            {props.queryType === QueryType.OneHop ? (
                                                                                <LinkedQueryEditor
                                                                                    key={"linked-editor" + observerProps.editorKey}
                                                                                    crossProject={
                                                                                        observerProps.editInfo.teamProject === undefined ||
                                                                                        observerProps.editInfo.teamProject.length === 0
                                                                                    }
                                                                                    editInfo={observerProps.editInfo}
                                                                                    fieldProjectData={observerProps.fieldProjectData}
                                                                                    fields={observerProps.fields}
                                                                                    projectId={this.projectId}
                                                                                    workItemLinkTypes={props.workItemLinkTypes}
                                                                                    setIsDirty={this.setIsDirty}
                                                                                    setFilterState={this.setFilterState}
                                                                                    setLinkState={this.setQueryModeAndLinkTypeState}
                                                                                    setStateAddRow={this.setStateAddRow}
                                                                                    setStateRemoveRow={this.setStateRemoveRow}
                                                                                    setClauseFieldState={this.setClauseFieldState}
                                                                                    setClauseOperatorState={this.setClauseOperatorState}
                                                                                    setClauseValueState={this.setClauseValueState}
                                                                                    setClauseLogicalOperatorState={
                                                                                        this.setClauseLogicalOperatorState
                                                                                    }
                                                                                />
                                                                            ) : (
                                                                                props.queryType === QueryType.Tree && (
                                                                                    <TreeQueryEditor
                                                                                        key={"tree-editor" + observerProps.editorKey}
                                                                                        crossProject={
                                                                                            observerProps.editInfo.teamProject ===
                                                                                                undefined ||
                                                                                            observerProps.editInfo.teamProject.length === 0
                                                                                        }
                                                                                        editInfo={observerProps.editInfo}
                                                                                        fieldProjectData={observerProps.fieldProjectData}
                                                                                        fields={observerProps.fields}
                                                                                        projectId={this.projectId}
                                                                                        workItemLinkTypes={props.workItemLinkTypes}
                                                                                        setIsDirty={this.setIsDirty}
                                                                                        setFilterState={this.setFilterState}
                                                                                        setTreeState={this.setQueryModeAndLinkTypeState}
                                                                                        setStateAddRow={this.setStateAddRow}
                                                                                        setStateRemoveRow={this.setStateRemoveRow}
                                                                                        setClauseFieldState={this.setClauseFieldState}
                                                                                        setClauseOperatorState={this.setClauseOperatorState}
                                                                                        setClauseValueState={this.setClauseValueState}
                                                                                        setClauseLogicalOperatorState={
                                                                                            this.setClauseLogicalOperatorState
                                                                                        }
                                                                                    />
                                                                                )
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </Observer>
                                                            </div>
                                                        );
                                                    }}
                                                </Observer>
                                            );

                                            return !observableProps.isLoaded || observableProps.isQueryLoading ? (
                                                <Spinner className="query-editor-spinner" label={Resources.Loading} />
                                            ) : (
                                                <>
                                                    <div className="flex-row query-editor-header-container-ff padding-vertical-4 font-size">
                                                        <div className="flex flex-center flex-grow flex-shrink rhythm-horizontal-4">
                                                            {this.enableNewBoardsQueriesNewDesignFF ? null : Resources.TypeOfQueryText}
                                                            <Dropdown
                                                                className="query-type padding-left-8"
                                                                items={this.queryTypeItems}
                                                                onSelect={(event: any, item: IListBoxItem<QueryType>) => {
                                                                    this.queryType.value = item.data!;
                                                                    if (item.id === "flat") {
                                                                        this.setQueryModeAndLinkTypeState(LinkQueryMode.WorkItems);
                                                                    } else if (item.id === "link") {
                                                                        this.setQueryModeAndLinkTypeState(LinkQueryMode.LinksMustContain);
                                                                    } else {
                                                                        this.setQueryModeAndLinkTypeState(LinkQueryMode.LinksRecursive);
                                                                    }
                                                                    this.setIsDirty(true);
                                                                }}
                                                                selection={this.queryTypeSelection}
                                                                width={300}
                                                                renderExpandable={this.enableNewBoardsQueriesNewDesignFF ? (props => (
                                                                    <DropdownExpandableButton
                                                                        {...props}
                                                                        renderSelectedItems={(selection: IListSelection, items: IListBoxItem[]) => {
                                                                            const item = items[selection.value[0].beginIndex]

                                                                            return (
                                                                                <>
                                                                                    <span className="secondary-text">{Resources.QueryType}</span>
                                                                                    {" " + item.text}
                                                                                </>
                                                                            )
                                                                        }}
                                                                    />
                                                                )) : undefined}
                                                            />
                                                        </div>
                                                        <div className="flex flex-center rhythm-horizontal-4">
                                                            <Checkbox
                                                                checked={observableProps.crossProject}
                                                                onChange={(
                                                                    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                                                                    checked: boolean
                                                                ) => {
                                                                    if (checked) {
                                                                        this.setEditInfoTeamProjectState("");
                                                                    } else {
                                                                        this.setEditInfoTeamProjectState(this.projectName);
                                                                    }
                                                                    this.queryAcrossProjectsChecked.value = checked;
                                                                    this.setIsDirty(true);
                                                                }}
                                                                label={Resources.QueryAcrossProjectsLabel}
                                                            />
                                                        </div>
                                                    </div>

                                                    <ScreenSizeObserver>
                                                        {(screenSizeProps: { screenSize: ScreenSize }) => {
                                                            const smallScreen = screenSizeProps.screenSize === ScreenSize.small || screenSizeProps.screenSize === ScreenSize.xsmall;

                                                            return !smallScreen ?
                                                                <Splitter
                                                                    nearElementClassName="editor-container"
                                                                    initialFixedSize={this.splitterSize}
                                                                    splitterDirection={SplitterDirection.Horizontal}
                                                                    className="scroll-hidden font-size"
                                                                    onFixedSizeChanged={this.onSplitterSizeChange}
                                                                    onRenderFarElement={() => (
                                                                        <Observer
                                                                            queryResult={{
                                                                                observableValue: this.queryService.queryResult,
                                                                                filter: this.onQueryResultUpdated
                                                                            }}
                                                                        >
                                                                            {() => (
                                                                                <div
                                                                                    ref={this.resultsDivRef}
                                                                                    className="page-content page-content-top scroll-hidden full-height"
                                                                                >
                                                                                    <ResultsTree
                                                                                        rootRef={this.resultsDivRef}
                                                                                        items={this.queryService.queryResultItems}
                                                                                        getItemsMap={() => this.queryService.queryResultItemsMap}
                                                                                        onDeleteItems={this.onDeleteItems}
                                                                                        onFocus={this.onFocus}
                                                                                        isShowMoreItemsSet={this.props.isShowMoreItemsSet}
                                                                                        queryType={this.queryResultsType}
                                                                                        selection={this.selection}
                                                                                        sourceIds={this.sourceIds}
                                                                                        targetIds={this.targetIds}
                                                                                        titleLinkOn={true}
                                                                                        treeRef={this.treeRef}
                                                                                        workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                                                                        useNewResultsTreeDesign={this.enableNewBoardsQueriesNewDesignFF}
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </Observer>
                                                                    )}
                                                                    onRenderNearElement={() => pageContent}
                                                                />
                                                                : pageContent
                                                        }}
                                                    </ScreenSizeObserver>
                                                </>
                                            );
                                            }}
                                    </Observer>

                                    :
                                    <Observer
                                        crossProject={this.queryAcrossProjectsChecked}
                                        isLoaded={this.fields.ready}
                                        isQueryLoading={this.isQueryLoading}
                                    >
                                        {(observableProps: { crossProject: boolean; isLoaded: boolean; isQueryLoading: boolean }) => {
                                            return !observableProps.isLoaded || observableProps.isQueryLoading ? (
                                                <Spinner className="query-editor-spinner" label={Resources.Loading} />
                                            ) : (
                                                <>
                                                    <div className="flex-row padding-horizontal-32 padding-vertical-4 font-size">
                                                        <div className="flex flex-center flex-grow flex-shrink rhythm-horizontal-4">
                                                            {this.enableNewBoardsQueriesNewDesignFF ? null : Resources.TypeOfQueryText}
                                                            <Dropdown
                                                                className="query-type padding-left-8"
                                                                items={this.queryTypeItems}
                                                                onSelect={(event: any, item: IListBoxItem<QueryType>) => {
                                                                    this.queryType.value = item.data!;
                                                                    if (item.id === "flat") {
                                                                        this.setQueryModeAndLinkTypeState(LinkQueryMode.WorkItems);
                                                                    } else if (item.id === "link") {
                                                                        this.setQueryModeAndLinkTypeState(LinkQueryMode.LinksMustContain);
                                                                    } else {
                                                                        this.setQueryModeAndLinkTypeState(LinkQueryMode.LinksRecursive);
                                                                    }
                                                                    this.setIsDirty(true);
                                                                }}
                                                                selection={this.queryTypeSelection}
                                                                width={300}
                                                                renderExpandable={this.enableNewBoardsQueriesNewDesignFF ? (props => (
                                                                    <DropdownExpandableButton
                                                                        {...props}
                                                                        renderSelectedItems={(selection: IListSelection, items: IListBoxItem[]) => {
                                                                            const item = items[selection.value[0].beginIndex]

                                                                            return (
                                                                                <>
                                                                                    <span className="secondary-text">{Resources.QueryType}</span>
                                                                                    {item.text}
                                                                                </>
                                                                            )
                                                                        }}
                                                                    />
                                                                )) : undefined}
                                                            />
                                                        </div>
                                                        <div className="flex flex-center rhythm-horizontal-4">
                                                            <Checkbox
                                                                checked={observableProps.crossProject}
                                                                onChange={(
                                                                    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                                                                    checked: boolean
                                                                ) => {
                                                                    if (checked) {
                                                                        this.setEditInfoTeamProjectState("");
                                                                    } else {
                                                                        this.setEditInfoTeamProjectState(this.projectName);
                                                                    }
                                                                    this.queryAcrossProjectsChecked.value = checked;
                                                                    this.setIsDirty(true);
                                                                }}
                                                                label={Resources.QueryAcrossProjectsLabel}
                                                            />
                                                        </div>
                                                    </div>
                                                    <Splitter
                                                        nearElementClassName="editor-container"
                                                        initialFixedSize={this.splitterSize}
                                                        splitterDirection={SplitterDirection.Horizontal}
                                                        className="scroll-hidden font-size"
                                                        onFixedSizeChanged={this.onSplitterSizeChange}
                                                        onRenderFarElement={() => (
                                                            <Observer
                                                                queryResult={{
                                                                    observableValue: this.queryService.queryResult,
                                                                    filter: this.onQueryResultUpdated
                                                                }}
                                                            >
                                                                {() => (
                                                                    <div
                                                                        ref={this.resultsDivRef}
                                                                        className="page-content page-content-top scroll-hidden full-height"
                                                                    >
                                                                        <ResultsTree
                                                                            rootRef={this.resultsDivRef}
                                                                            items={this.queryService.queryResultItems}
                                                                            getItemsMap={() => this.queryService.queryResultItemsMap}
                                                                            onDeleteItems={this.onDeleteItems}
                                                                            onFocus={this.onFocus}
                                                                            isShowMoreItemsSet={this.props.isShowMoreItemsSet}
                                                                            queryType={this.queryResultsType}
                                                                            selection={this.selection}
                                                                            sourceIds={this.sourceIds}
                                                                            targetIds={this.targetIds}
                                                                            titleLinkOn={true}
                                                                            treeRef={this.treeRef}
                                                                            workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                                                            useNewResultsTreeDesign={this.enableNewBoardsQueriesNewDesignFF}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </Observer>
                                                        )}
                                                        onRenderNearElement={() => (
                                                            <Observer
                                                                fieldProjectData={this.fieldProjectData}
                                                                fields={this.fields}
                                                                editInfo={this.editInfo}
                                                                editorKey={this.editorKey}
                                                            >
                                                                {(observerProps: {
                                                                    fieldProjectData: IFieldProjectData;
                                                                    fields: IFieldEntry[];
                                                                    editInfo: IEditInfo;
                                                                    editorKey: number;
                                                                }) => {
                                                                    return (
                                                                        <div className="flex-column page-content page-content-top v-scroll-auto full-height separator-line-top">
                                                                            <div className="rhythm-vertical-16">
                                                                                <span>{Resources.QueriesEditorTopLevelText}</span>
                                                                                <EditorTable
                                                                                    key={"editor-table-" + observerProps.editorKey}
                                                                                    clauseType={ClauseType.Source}
                                                                                    crossProject={
                                                                                        observerProps.editInfo.teamProject === undefined ||
                                                                                        observerProps.editInfo.teamProject.length === 0
                                                                                    }
                                                                                    editInfo={observerProps.editInfo}
                                                                                    fieldProjectData={observerProps.fieldProjectData}
                                                                                    fields={observerProps.fields}
                                                                                    projectId={this.projectId}
                                                                                    setIsDirty={this.setIsDirty}
                                                                                    setFilterState={this.setFilterState}
                                                                                    setStateAddRow={this.setStateAddRow}
                                                                                    setStateRemoveRow={this.setStateRemoveRow}
                                                                                    setClauseFieldState={this.setClauseFieldState}
                                                                                    setClauseOperatorState={this.setClauseOperatorState}
                                                                                    setClauseValueState={this.setClauseValueState}
                                                                                    setClauseLogicalOperatorState={this.setClauseLogicalOperatorState}
                                                                                />
                                                                            </div>
                                                                            <Observer
                                                                                queryType={this.queryType}
                                                                                workItemLinkTypes={this.workItemLinkTypes}
                                                                            >
                                                                                {(props: {
                                                                                    queryType: QueryType;
                                                                                    workItemLinkTypes: IWorkItemLinkType[];
                                                                                }) => (
                                                                                    <>
                                                                                        {props.queryType === QueryType.OneHop ? (
                                                                                            <LinkedQueryEditor
                                                                                                key={"linked-editor" + observerProps.editorKey}
                                                                                                crossProject={
                                                                                                    observerProps.editInfo.teamProject === undefined ||
                                                                                                    observerProps.editInfo.teamProject.length === 0
                                                                                                }
                                                                                                editInfo={observerProps.editInfo}
                                                                                                fieldProjectData={observerProps.fieldProjectData}
                                                                                                fields={observerProps.fields}
                                                                                                projectId={this.projectId}
                                                                                                workItemLinkTypes={props.workItemLinkTypes}
                                                                                                setIsDirty={this.setIsDirty}
                                                                                                setFilterState={this.setFilterState}
                                                                                                setLinkState={this.setQueryModeAndLinkTypeState}
                                                                                                setStateAddRow={this.setStateAddRow}
                                                                                                setStateRemoveRow={this.setStateRemoveRow}
                                                                                                setClauseFieldState={this.setClauseFieldState}
                                                                                                setClauseOperatorState={this.setClauseOperatorState}
                                                                                                setClauseValueState={this.setClauseValueState}
                                                                                                setClauseLogicalOperatorState={
                                                                                                    this.setClauseLogicalOperatorState
                                                                                                }
                                                                                            />
                                                                                        ) : (
                                                                                            props.queryType === QueryType.Tree && (
                                                                                                <TreeQueryEditor
                                                                                                    key={"tree-editor" + observerProps.editorKey}
                                                                                                    crossProject={
                                                                                                        observerProps.editInfo.teamProject ===
                                                                                                            undefined ||
                                                                                                        observerProps.editInfo.teamProject.length === 0
                                                                                                    }
                                                                                                    editInfo={observerProps.editInfo}
                                                                                                    fieldProjectData={observerProps.fieldProjectData}
                                                                                                    fields={observerProps.fields}
                                                                                                    projectId={this.projectId}
                                                                                                    workItemLinkTypes={props.workItemLinkTypes}
                                                                                                    setIsDirty={this.setIsDirty}
                                                                                                    setFilterState={this.setFilterState}
                                                                                                    setTreeState={this.setQueryModeAndLinkTypeState}
                                                                                                    setStateAddRow={this.setStateAddRow}
                                                                                                    setStateRemoveRow={this.setStateRemoveRow}
                                                                                                    setClauseFieldState={this.setClauseFieldState}
                                                                                                    setClauseOperatorState={this.setClauseOperatorState}
                                                                                                    setClauseValueState={this.setClauseValueState}
                                                                                                    setClauseLogicalOperatorState={
                                                                                                        this.setClauseLogicalOperatorState
                                                                                                    }
                                                                                                />
                                                                                            )
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </Observer>
                                                                        </div>
                                                                    );
                                                                }}
                                                            </Observer>
                                                        )}
                                                    />
                                                </>
                                            );
                                        }}
                                    </Observer>
                                }

                            </Page>
                        </Surface>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }

    private registerShortcuts = (): void => {
        this.shortcutService.registerShortcut(Resources.EditorTab, {
            combos: ["mod+a"],
            description: Resources.SelectAll,
            action: () => {
                this.onSelectAll();
            }
        });
    };

    private unregisterShortcuts = () => {
        this.shortcutService.removeShortcutGroup(Resources.ResultsTab);
    };

    private onFocus = async (event: React.SyntheticEvent<HTMLElement>, listRow: ITreeRow<IQueryResultItem>) => {
        const itemId = Number(listRow.data.underlyingItem.data.value[CoreFieldRefNames.Id]);
        if (!itemId) {
            return;
        }

        this.lastFocusedRow.value = listRow.index;
    };

    private onFocusNext = () => {
        QueryUtils.onFocusNext(this.treeRef, this.lastFocusedRow, this.queryService.queryResultItems.length, this.selection);
    };

    private onFocusPrevious = () => {
        QueryUtils.onFocusPrevious(this.treeRef, this.lastFocusedRow, this.selection);
    };

    private onSelectAll = () => {
        if (this.queryService.filteredQueryResultItems.value && this.queryService.filteredQueryResultItems.value.length > 0) {
            this.selection.select(0, this.queryService.filteredQueryResultItems.value.length);
        }
    };

    private onQueryResultUpdated = () => {
        this.queryType.value = this.queryService.getQueryType();
        this.queryResultsType.value = this.queryType.value;
        this.sourceIds.value = this.queryService.queryResult.value?.data?.sourceIds || [];
        this.targetIds.value = this.queryService.queryResult.value?.data?.targetIds || [];
        if (this.queryService.queryResult.value?.data?.error) {
            reportError(this.context.pageContext, "query-service", this.queryService.queryResult.value?.data?.error);
        }
        return false;
    };

    private onRevert = () => {
        if (this.queryService.queryId || this.queryService.tempQueryId || this.queryService.urlWiql) {
            this.onRunQuery();
        }
        this.updateQueryType();
        this.editorKey.value++;
    };

    private onSplitterSizeChange = (value: number) => {
        this.splitterSize = value;
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = {
            [`${QueriesHubConstants.EditorTableSplitterSize}/${QueriesHubConstants.SettingsKey}`]: value
        };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    };

    private async loadData() {
        await this.getQuery();
        if (this.queryService.queryResult.value) {
            window.setTimeout(this.focusInitialRow, 0);
        } else if (
            this.queryService.queryResultItems.ready &&
            (!this.queryService.isNewQuery || this.queryService.tempQueryId || this.queryService.urlWiql)
        ) {
            // run query on load if it is not a new query, or if it is a temporary query/query with the wiql in the URL
            this.onRunQuery();
        }
    }

    private focusInitialRow = () => {
        QueryUtils.focusInitialRow(this.queryService, this.treeRef, this.lastFocusedRow, this.queryService.queryResultItems, this.selection);
    };

    private setIsDirty = (isDirty: boolean) => {
        this.isDirty.value = isDirty;
    };

    private setEditInfoTeamProjectState = (teamProject: string) => {
        this.editInfo.value = { ...this.editInfo.value, teamProject };
    };

    private setFilterState = (filter: IFilter, clauseType: ClauseType) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter = filter;
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter = filter;
        } else {
            currStateEditInfo.sourceFilter = filter;
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setStateAddRow = (clauseType: ClauseType, rowIndex?: number) => {
        const currStateEditInfo = { ...this.editInfo.value };
        const index =
            rowIndex ??
            (clauseType === ClauseType.Tree
                ? currStateEditInfo.treeTargetFilter.clauses.length
                : clauseType === ClauseType.Link
                ? currStateEditInfo.linkTargetFilter.clauses.length
                : currStateEditInfo.sourceFilter.clauses.length);
        const newClause = {
            fieldName: "",
            operator: WiqlOperators.OperatorEqualTo,
            value: "",
            index: index,
            originalIndex: index,
            logicalOperator: "And"
        } as IClause;
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter.clauses.splice(index, 0, newClause);
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter.clauses.splice(index, 0, newClause);
        } else {
            currStateEditInfo.sourceFilter.clauses.splice(index, 0, newClause);
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setStateRemoveRow = (clauseType: ClauseType, rowIndex: number) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter.clauses.splice(rowIndex, 1);
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter.clauses.splice(rowIndex, 1);
        } else {
            currStateEditInfo.sourceFilter.clauses.splice(rowIndex, 1);
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setClauseFieldState = (field: string, rowIndex: number, clauseType: ClauseType) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter.clauses[rowIndex].fieldName = field;
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter.clauses[rowIndex].fieldName = field;
        } else {
            currStateEditInfo.sourceFilter.clauses[rowIndex].fieldName = field;
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setClauseOperatorState = (operator: string, rowIndex: number, clauseType: ClauseType) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter.clauses[rowIndex].operator = operator;
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter.clauses[rowIndex].operator = operator;
        } else {
            currStateEditInfo.sourceFilter.clauses[rowIndex].operator = operator;
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setClauseValueState = (value: string, rowIndex: number, clauseType: ClauseType) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter.clauses[rowIndex].value = value;
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter.clauses[rowIndex].value = value;
        } else {
            currStateEditInfo.sourceFilter.clauses[rowIndex].value = value;
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setClauseLogicalOperatorState = (logicalOperator: string, rowIndex: number, clauseType: ClauseType) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (clauseType === ClauseType.Tree) {
            currStateEditInfo.treeTargetFilter.clauses[rowIndex].logicalOperator = logicalOperator;
        } else if (clauseType === ClauseType.Link) {
            currStateEditInfo.linkTargetFilter.clauses[rowIndex].logicalOperator = logicalOperator;
        } else {
            currStateEditInfo.sourceFilter.clauses[rowIndex].logicalOperator = logicalOperator;
        }
        this.editInfo.value = currStateEditInfo;
    };

    private setQueryModeAndLinkTypeState = (mode?: number, linkType?: string, treeLinkType?: string, checked?: boolean) => {
        const currStateEditInfo = { ...this.editInfo.value };
        if (mode) {
            currStateEditInfo.mode = mode;
            const queryType = LinkQueryMode.getQueryType(mode);
            // initialize filters and arrays if you change the query mode
            if (queryType === QueryType.Tree && currStateEditInfo.treeLinkTypes === undefined && currStateEditInfo.treeTargetFilter === undefined) {
                currStateEditInfo.treeLinkTypes = "";
                currStateEditInfo.treeTargetFilter = { clauses: [], groups: [] };
            } else if (
                queryType === QueryType.OneHop &&
                currStateEditInfo.linkTypes === undefined &&
                currStateEditInfo.linkTargetFilter === undefined
            ) {
                currStateEditInfo.linkTypes = "";
                currStateEditInfo.linkTargetFilter = { clauses: [], groups: [] };
            } else if (queryType === QueryType.Flat && currStateEditInfo.sourceFilter === undefined) {
                currStateEditInfo.sourceFilter = { clauses: [], groups: [] };
            }
        } else if (linkType) {
            if (linkType === "any") {
                currStateEditInfo.linkTypes = "";
            } else if (checked !== undefined) {
                if (checked) {
                    if (currStateEditInfo.linkTypes.length === 0) {
                        currStateEditInfo.linkTypes = linkType;
                    } else {
                        const index = currStateEditInfo.linkTypes.split(",").findIndex(link => link === linkType);
                        if (index === -1) {
                            currStateEditInfo.linkTypes = currStateEditInfo.linkTypes.concat("," + linkType);
                        }
                    }
                } else {
                    const arr = currStateEditInfo.linkTypes.split(",");
                    const index = arr.findIndex(link => link === linkType);
                    if (index !== -1) {
                        arr.splice(index, 1);
                        currStateEditInfo.linkTypes = arr.join(",");
                    }
                }
            }
        } else if (treeLinkType) {
            currStateEditInfo.treeLinkTypes = treeLinkType;
        }
        this.editInfo.value = currStateEditInfo;
    };

    private createQueryTypeItems = () => {
        const queryTypeItems: IQueryTypeItem[] = [];
        queryTypeItems.push({
            id: "flat",
            text: Resources.FlatQueryLongText,
            icon: "ViewList",
            selected: true,
            disabled: false,
            data: QueryType.Flat
        });
        queryTypeItems.push({ id: "link", text: Resources.DirectLinksQueryLongText, icon: "ViewListGroup", disabled: false, data: QueryType.OneHop });
        queryTypeItems.push({ id: "tree", text: Resources.TreeLinksQueryLongText, icon: "ViewListTree", disabled: false, data: QueryType.Tree });
        this.queryTypeItems = queryTypeItems.map(
            queryTypeItem =>
                ({
                    iconProps: { iconName: queryTypeItem.icon },
                    id: queryTypeItem.id,
                    data: queryTypeItem.data,
                    text: queryTypeItem.text
                } as IListBoxItem<QueryType>)
        );
        this.queryTypeSelection = new DropdownSelection();
        this.queryTypeSelection.select(0);
        this.queryType.value = QueryType.Flat;
    };

    private async getQuery() {
        await this.queryService.getQuery(this.projectId);
        this.updateQueryType();
        this.isQueryLoading.value = false;
    }

    private updateQueryType = () => {
        this.queryType.value = LinkQueryMode.getQueryType(this.editInfo.value.mode);
        this.queryTypeSelection.select(this.queryTypeItems.findIndex(item => item.data === this.queryType.value));
    };

    private async runQuery(wiql: string) {
        await this.queryService.executeQuery(this.projectId, wiql, this.props.isShowMoreItemsSet?.value);
        this.queryService.filteredQueryResultItems.value = this.queryService.queryResultItems.value;
    }

    private onRunQuery = async () => {
        const wiql = await this.queryService.generateWiql(this.projectId);
        this.selection.clear();
        this.lastFocusedRow.value = -1;
        this.clearWarning();
        await this.runQuery(wiql);
        this.focusInitialRow();
    };

    private updateCrossProjectCheckbox = () => {
        if (this.editInfo.value.teamProject !== undefined && this.editInfo.value.teamProject.length !== 0) {
            this.queryAcrossProjectsChecked.value = false;
        } else {
            this.queryAcrossProjectsChecked.value = true;
        }
    };

    private onDeleteItems = (workItems: INamedFieldDataDictionary[]) => {
        const ids = new Set(workItems.map(wi => wi[CoreFieldRefNames.Id]));
        this.queryService.removeWorkItems(ids);

        this.selection.clear();
    };

    private clearWarning = () => {
        const currentBanner = this.globalMessageService.getBanner();
        if (currentBanner) {
            this.globalMessageService.closeBanner();
        }
    };
}

Components.add("query-editor-component", QueryEditor);
